import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Search, Tv as TvIcon, RefreshCw, AlertTriangle, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { useRealtimeTvs } from '@/hooks/useRealtimeTvs';
import { timeAgo } from '@/utils/cn';

export function TvsPage() {
  const { tenantId } = useAuthStore();
  useRealtimeTvs(tenantId);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [storeFilter, setStoreFilter] = useState<string>('all');

  const { data: stores = [] } = useQuery({
    queryKey: ['stores'],
    queryFn: () => apiClient.getStores().then(r => r.data),
  });
  const storeById = useMemo(
    () => Object.fromEntries(stores.map((s: any) => [s.id, s])),
    [stores],
  ) as Record<number, any>;
  const showStoreFilter = stores.length > 1;

  const { data: tvs = [], isLoading } = useQuery({
    queryKey: ['tvs', search, statusFilter, storeFilter],
    queryFn: () => apiClient.getTvs({
      search: search || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      store_id: storeFilter === 'all' ? undefined : Number(storeFilter),
    }).then(r => r.data),
  });

  return (
    <div className="container-app py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">TVs</h1>
          <p className="text-muted-foreground">Gerencie todas as suas telas conectadas</p>
        </div>
        <Button asChild variant="gradient"><Link to="/tvs/add"><Plus className="h-4 w-4" />Adicionar TV</Link></Button>
      </div>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar TVs..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="all">Todos status</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="pending">Pareando</option>
          <option value="error">Erro</option>
        </select>
        {showStoreFilter && (
          <select
            value={storeFilter}
            onChange={e => setStoreFilter(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">Todas as lojas</option>
            {stores.map((s: any) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Carregando…</div>
      ) : tvs.length === 0 ? (
        <div className="text-center py-12">
          <TvIcon className="h-12 w-12 mx-auto text-muted-foreground" />
          <h3 className="mt-4 font-semibold">Nenhuma TV encontrada</h3>
          <Button asChild className="mt-4"><Link to="/tvs/add">Adicionar primeira TV</Link></Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tvs.map((tv: any) => <TvCard key={tv.id} tv={tv} store={tv.store_id ? storeById[tv.store_id] : null} showStore={showStoreFilter} />)}
        </div>
      )}
    </div>
  );
}

function TvCard({ tv, store, showStore }: { tv: any; store: any; showStore: boolean }) {
  const statusVariant: Record<string, any> = {
    online: 'success', offline: 'danger', pending: 'warning', error: 'danger',
  };
  const statusLabel: Record<string, string> = {
    online: 'Online', offline: 'Offline', pending: 'Pareando', error: 'Erro',
  };
  return (
    <Link to={`/tvs/${tv.id}`} className="rounded-lg border bg-card p-5 hover:border-brand-300 hover:shadow-md transition-all">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="grid place-items-center h-10 w-10 rounded-md bg-brand-100 text-brand-700">
            <TvIcon className="h-5 w-5" />
          </div>
          <div>
            <div className="font-semibold">{tv.name}</div>
            <div className="text-xs text-muted-foreground">{tv.location || 'Sem localização'}</div>
          </div>
        </div>
        <Badge variant={statusVariant[tv.status]}>{statusLabel[tv.status]}</Badge>
      </div>
      {showStore && (
        <div className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted/60 rounded px-2 py-0.5">
          <Building2 className="h-3 w-3" />
          {store ? store.name : 'Sem loja'}
        </div>
      )}
      <div className="mt-4 text-xs text-muted-foreground space-y-1">
        <div>Última atividade: {timeAgo(tv.last_seen_at)}</div>
        {tv.resolution_width && <div>{tv.resolution_width}×{tv.resolution_height} · {tv.orientation}</div>}
        {tv.recent_reload_count > 0 && (
          <ReloadHealthHint
            count={tv.recent_reload_count}
            reason={tv.last_reload_reason}
            at={tv.last_reload_at}
          />
        )}
      </div>
    </Link>
  );
}

// Quando o app TV auto-recupera (watchdog mata e relança), o motivo chega no
// heartbeat e fica visível aqui. Operador vê de relance se uma TV específica
// está se recuperando demais (sinal de hardware fraco ou playlist quebrada).
function ReloadHealthHint({
  count, reason, at,
}: { count: number; reason: string | null; at: string | null }) {
  const labels: Record<string, string> = {
    heartbeat_timeout: 'JS travou',
    heap_pressure: 'memória cheia',
    stall: 'slide parado',
    manual: 'voltar manual',
    ota: 'atualização',
  };
  const friendly = (reason && labels[reason]) || reason || 'desconhecido';
  const severe = count >= 5;
  const Icon = severe ? AlertTriangle : RefreshCw;
  return (
    <div className={
      'mt-2 flex items-center gap-1.5 rounded px-2 py-1 text-xs ' +
      (severe
        ? 'bg-red-50 text-red-700 border border-red-200'
        : 'bg-amber-50 text-amber-800 border border-amber-200')
    }>
      <Icon className="h-3.5 w-3.5 flex-shrink-0" />
      <span>
        {count} reinício{count > 1 ? 's' : ''} em 24h
        <span className="opacity-70"> · último: {friendly}{at ? ` · ${timeAgo(at)}` : ''}</span>
      </span>
    </div>
  );
}
